// App.js
import React from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import { TenantContentProvider } from "../src/Context/TenantContentContext";

import Home from "./Pages/Home/Home";
import BlogIndex from "./Pages/Blog/Blog-index";
import BlogDetail from "./Pages/Blog/Blog-detail";
import Support from "./Pages/Support/Support";
import BlogEditable from "./Pages/Blog/Blog-editable";
import Blog from "./Pages/Blog/Blog";
import { ViewAction } from "./Enums/ViewActionEnum";
import Reserve from "./Pages/Reservation/Reserve";
import Reservation from "./Pages/Reservation/Reservation";
import { Login } from "./Pages/Auth/Login";
import Profile from "./Pages/Auth/Profile";
import { isLoggedIn } from "./Services/AuthService";
import Unauthorized from "./Pages/Auth/Unauthorized";
import ReserveDetail from "./Pages/Reservation/ReserveDetail";
import ReserveCallback from "./Pages/Callback/ReserveCallback";
import About from "./Pages/About/About";
import UserReserves from "./Pages/Reservation/UserReserves";
import AllReserves from "./Pages/Reservation/AllReserves";
import Layout from "./Layout";
import { useLocation } from "react-router-dom";
import ReserveException from "./Pages/Reservation/ReserveException";
import UserIndex from "./Pages/Auth/User-index";
import ManualReserve from "./Pages/Reservation/ManualReserve";
import Payment from "./Pages/Payment/Payment";
import SuccessPayments from "./Pages/Payment/SuccessPayment";
import ConsultationIndex from "./Pages/Consultation/Consultation-index";
import Consultation from "./Pages/Consultation/Consultation";
import ConsulttationCallback from "./Pages/Callback/ConsulttationCallback";
import ChatComponent from "./Pages/Consultation/ChatPage";
import ChatIndex from "./Pages/Consultation/Chat-Index/Chat-Index";
import DoctorChatIndex from "./Pages/Consultation/Doctor-Chat-Index";
import AppSetting from "./Pages/Setting/AppSetting";
import Setting from "./Pages/Setting/Setting";
import UserProfile from "./Pages/UserProfile/UserProfile";
import UnCheckComments from "./Pages/Comment/UnCheckComments";
import ManualConsultation from "./Pages/Consultation/CreateConsultation";
import Dashboard from "./Pages/Dashboard/Dashboard";
import SubscriptionManagement from "./Pages/Subscription/SubscriptionManagement";
import SubscriptionPlansAdmin from "./Pages/Subscription/SubscriptionPlansAdmin";
import SubscriptionCallback from "./Pages/Callback/SubscriptionCallback";
import AuditLogs from "./Pages/Audit/AuditLogs";
import NotFound from "./Pages/NotFound";

const PrivateRoute = ({ children, roles }) => {
  const loggedIn = isLoggedIn();
  const location = useLocation();
  if (!loggedIn) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  if (
    roles &&
    !roles.some((role) =>
      localStorage.getItem("roles").split(",").includes(role),
    )
  ) {
    return <Navigate to="/unauthorized" />;
  }

  return children;
};

const router = createBrowserRouter([
  {
    element: <Layout />, // Use Layout as the root element
    children: [
      { path: "/", element: <Home /> },
      {
        path: "blog",
        element: <Blog />,
        children: [
          { path: "index", element: <BlogIndex /> },
          {
            path: "create",
            element: <BlogEditable action={ViewAction.CREATE} />,
          },
          {
            path: "edit/:id",
            element: (
              <PrivateRoute roles={["Admin", "Doctor"]}>
                <BlogEditable action={ViewAction.EDIT} />
              </PrivateRoute>
            ),
          },
          { path: "detail/:slug", element: <BlogDetail /> },
          { path: "Detail/:slug", element: <BlogDetail /> },
        ],
      },
      {
        path: "payment",
        element: <Payment />,
        children: [
          {
            path: "succesPayments",
            element: (
              <PrivateRoute roles={["Admin", "Doctor"]}>
                <SuccessPayments />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: "reservation",
        element: <Reservation />,
        children: [
          { path: "reserve", element: <Reserve /> },
          {
            path: "detail/success/:id",
            element: (
              <PrivateRoute roles={["User"]}>
                <ReserveDetail state={"success"} />
              </PrivateRoute>
            ),
          },
          {
            path: "reserveException",
            element: (
              <PrivateRoute roles={["Admin", "Doctor", "Secretary"]}>
                <ReserveException />
              </PrivateRoute>
            ),
          },
          {
            path: "detail/fail",
            element: (
              <PrivateRoute roles={["User"]}>
                <ReserveDetail state={"fail"} />
              </PrivateRoute>
            ),
          },
          {
            path: "manualReserve",
            element: (
              <PrivateRoute roles={["Admin", "Doctor", "Secretary"]}>
                <ManualReserve />
              </PrivateRoute>
            ),
          },
          {
            path: "callback",
            element: (
              <PrivateRoute roles={["User"]}>
                <ReserveCallback />
              </PrivateRoute>
            ),
          },
          {
            path: "userReserves",
            element: (
              <PrivateRoute roles={["User"]}>
                <UserReserves />
              </PrivateRoute>
            ),
          },
          {
            path: "allReserves",
            element: (
              <PrivateRoute roles={["Admin", "Doctor", "Secretary"]}>
                <AllReserves />
              </PrivateRoute>
            ),
          },
        ],
      },
      { path: "login", element: <Login /> },
      { path: "support", element: <Support /> },
      {
        path: "complete-profile",
        element: <Profile action={ViewAction.CREATE} />,
      },
      {
        path: "edit-profile",
        element: (
          <PrivateRoute roles={["User"]}>
            <Profile action={ViewAction.EDIT} />
          </PrivateRoute>
        ),
      },
      {
        path: "user",
        element: (
          <PrivateRoute roles={["User"]}>
            <UserProfile action={ViewAction.EDIT} />
          </PrivateRoute>
        ),
      },
      {
        path: "edit-user/:id",
        element: (
          <PrivateRoute roles={["Admin", "Doctor", "Secretary"]}>
            <Profile action={ViewAction.AdminEdit} />
          </PrivateRoute>
        ),
      },
      {
        path: "user-index",
        element: (
          <PrivateRoute roles={["Admin", "Doctor", "Secretary"]}>
            <UserIndex />
          </PrivateRoute>
        ),
      },
      { path: "unauthorized", element: <Unauthorized /> },
      { path: "*", element: <NotFound /> },

      {
        path: "comment/uncheck",
        element: (
          <PrivateRoute roles={["Admin", "Doctor", "Secretary"]}>
            <UnCheckComments />
          </PrivateRoute>
        ),
      },

      {
        path: "counseling",
        element: <Consultation />,
        children: [
          { path: "index", element: <ConsultationIndex /> },
          {
            path: "callback",
            element: (
              <PrivateRoute roles={["User"]}>
                <ConsulttationCallback />
              </PrivateRoute>
            ),
          },
          {
            path: "fail",
            element: (
              <PrivateRoute roles={["User"]}>
                <ReserveDetail state={"fail"} source="Consultation" />
              </PrivateRoute>
            ),
          },
          {
            path: "createConsultation",
            element: (
              <PrivateRoute roles={["Doctor", "Secretary"]}>
                <ManualConsultation />
              </PrivateRoute>
            ),
          },
          // {
          //   path: "chat/:sessionId",
          //   element: (
          //     <PrivateRoute roles={["User"]}>
          //       <ChatComponent />
          //     </PrivateRoute>
          //   ),
          // },
          {
            path: "chat/index",
            element: (
              <PrivateRoute roles={["User"]}>
                <ChatIndex />
              </PrivateRoute>
            ),
          },
          {
            path: "doctorChats",
            element: (
              <PrivateRoute roles={["Admin", "Doctor"]}>
                <DoctorChatIndex />
              </PrivateRoute>
            ),
          },
        ],
      },
      { path: "about", element: <About /> },
      {
        path: "counseling/chat/:sessionId",
        element: (
          <PrivateRoute roles={["User"]}>
            <ChatComponent />
          </PrivateRoute>
        ),
      },
      {
        path: "Setting",
        element: <Setting />,
        children: [
          {
            path: "app-setting",
            element: (
              <PrivateRoute roles={["Admin", "Doctor", "Secretary"]}>
                <AppSetting />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: "Dashboard",
        element: (
          <PrivateRoute roles={["Admin", "Doctor"]}>
            <Dashboard />
          </PrivateRoute>
        ),
      },
      {
        path: "subscription",
        element: (
          <PrivateRoute roles={["Admin", "Doctor", "Secretary"]}>
            <SubscriptionManagement />
          </PrivateRoute>
        ),
      },
      {
        path: "admin/subscription/plans",
        element: (
          <PrivateRoute roles={["Admin"]}>
            <SubscriptionPlansAdmin />
          </PrivateRoute>
        ),
      },
      {
        path: "admin/subscription/verify",
        element: (
          <PrivateRoute roles={["Admin", "Doctor", "Secretary"]}>
            <SubscriptionCallback />
          </PrivateRoute>
        ),
      },
      {
        path: "admin/audit-logs",
        element: (
          <PrivateRoute roles={["Admin"]}>
            <AuditLogs />
          </PrivateRoute>
        ),
      },
    ],
  },
]);

const App = () => {
  return (
    <TenantContentProvider>
      <RouterProvider router={router} fallbackElement={<div>Loading...</div>} />
    </TenantContentProvider>
  );
};

export default App;
